<template>
  <!-- ══════════════════════════════════════════════ -->
  <!-- Botón compacto para reabrir (solo si cerrado) -->
  <!-- ══════════════════════════════════════════════ -->
  <button
    v-if="showCollapsedTrigger"
    type="button"
    @click="open"
    class="fixed bottom-3 right-3 h-12 rounded-full flex items-center justify-center gap-2
           bg-gray-900 text-white shadow-lg hover:shadow-xl hover:bg-gray-800
           transition-all duration-200 z-50 group
           max-sm:bottom-[calc(0.75rem+env(safe-area-inset-bottom,0px))]"
    :class="isCompactSearchViewport ? 'w-12 px-0' : 'px-5'"
    :aria-label="t('colivingNaturalSearch.buttonLabel')"
  >
    <div class="relative">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" 
           class="h-5 w-5 text-indigo-400 group-hover:scale-110 transition-transform">
        <path fill-rule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5Z" clip-rule="evenodd" />
      </svg>
      <!-- Badge email sin verificar (ámbar, pulsante) -->
      <span
        v-if="showEmailVerificationPrompt"
        class="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-amber-400 animate-pulse"
      ></span>
      <!-- Badge de alertas urgentes (rojo) -->
      <span 
        v-else-if="urgentCount > 0"
        class="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] flex items-center justify-center 
               rounded-full bg-red-500 text-[10px] font-bold text-white px-1"
      >
        {{ urgentCount > 9 ? '9+' : urgentCount }}
      </span>
    </div>
    <span class="font-medium text-sm whitespace-nowrap hidden sm:inline">{{ t('colivingNaturalSearch.buttonText') }}</span>
  </button>

  <!-- ══════════════════════════════════════════════ -->
  <!-- Widget popup flotante                         -->
  <!-- ══════════════════════════════════════════════ -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-4 scale-95"
  >
    <div
      v-if="isOpen"
      class="fixed z-50 overflow-hidden flex flex-col
             bottom-1 right-1 w-[400px] max-h-[85vh]
             bg-white rounded-2xl border border-gray-200 shadow-2xl
             max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:w-full max-sm:rounded-b-none max-sm:max-h-[80vh]"
    >
      <!-- ═══════════════════════════════════════════ -->
      <!-- EMAIL SIN VERIFICAR (applicant no verificado) -->
      <!-- ═══════════════════════════════════════════ -->
      <template v-if="showEmailVerificationPrompt">
        <!-- Header igual al del asistente -->
        <div class="relative px-5 py-4 flex-shrink-0 overflow-hidden bg-gray-950">
          <div class="absolute inset-0 opacity-[0.04]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
          <div class="flex items-center justify-between relative z-10">
            <div>
              <h3 class="text-base font-bold text-white tracking-tight flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4 text-indigo-400">
                  <path fill-rule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5Z" clip-rule="evenodd" />
                </svg>
                {{ t('assistant.title') }}
              </h3>
              <p class="text-gray-400 text-xs mt-0.5">Activa tu cuenta</p>
            </div>
            <button
              @click="close"
              class="text-gray-500 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              :aria-label="t('common.close')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenido -->
        <div class="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center gap-5">
          <div class="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-bold text-gray-900 mb-1">Verifica tu email para continuar</p>
            <p class="text-xs text-gray-500 leading-relaxed">
              Hemos enviado un enlace de verificación a tu correo. Revisa tu bandeja de entrada (y la carpeta de spam).
            </p>
          </div>
          <router-link
            to="/email-verification-sent"
            @click="close"
            class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reenviar enlace de verificación
          </router-link>
        </div>
      </template>

      <!-- ═══════════════════════════════════════ -->
      <!-- MODO ASISTENTE (applicant en /contracts) -->
      <!-- ═══════════════════════════════════════ -->
      <AssistantWidgetPanel
        v-else-if="isAssistantMode"
        @close="close"
        @urgent-count="handleUrgentCount"
      />

      <!-- ═══════════════════════════════════════ -->
      <!-- MODO BÚSQUEDA (por defecto)             -->
      <!-- ═══════════════════════════════════════ -->
      <template v-else>
      <!-- ── Header (fondo oscuro estilo Hero) ── -->
      <div class="relative px-5 py-4 flex-shrink-0 overflow-hidden bg-gray-950">
        <!-- Grain texture sutil -->
        <div class="absolute inset-0 opacity-[0.04]" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')"></div>
        
        <div class="flex items-center justify-between relative z-10">
          <div>
            <router-link 
              :to="searchPagePath" 
              @click="close"
              class="text-base font-bold text-white tracking-tight flex items-center gap-2 hover:text-indigo-300 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4 text-indigo-400">
                <path fill-rule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5Z" clip-rule="evenodd" />
              </svg>
              {{ t('colivingNaturalSearch.title') }}
            </router-link>
            <p class="text-gray-400 text-xs mt-0.5">{{ t('colivingNaturalSearch.subtitle') }}</p>
          </div>
          <button 
            @click="close" 
            class="text-gray-500 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            :aria-label="t('common.close')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Contenido con scroll ── -->
      <div class="flex-1 overflow-y-auto" ref="contentRef">
        <div class="p-4">
          <!-- Input de búsqueda -->
          <div class="relative">
            <textarea
              ref="searchInputRef"
              v-model="searchText"
              @input="handleInput"
              @keydown.enter.prevent="handleEnter"
              class="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm 
                     focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent 
                     shadow-sm resize-none placeholder-gray-400 bg-white"
              :placeholder="searchPlaceholder"
              rows="2"
            ></textarea>
            <button
              v-if="searchText.trim()"
              @click="executeSearch"
              :disabled="isSearching"
              class="absolute top-1/2 right-3 -translate-y-1/2 h-8 w-8 rounded-full 
                     bg-gray-900 hover:bg-gray-800 text-white flex items-center justify-center 
                     transition-colors disabled:opacity-50"
              :aria-label="t('colivingNaturalSearch.search')"
            >
              <svg v-if="!isSearching" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <svg v-else class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            </button>
          </div>

          <!-- Personalización (usuario logueado) -->
          <div v-if="isAuthenticated && hasPreferences" class="mt-2 flex items-center gap-1.5">
            <div class="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
            <span class="text-xs text-gray-500 font-medium">{{ t('colivingNaturalSearch.personalized') }}</span>
          </div>

          <!-- Botones inferiores: búsqueda avanzada + servicios para empresas -->
          <div v-if="!searchText.trim() && !hasResults" class="mt-3 flex items-center gap-3 flex-wrap">
            <router-link 
              :to="searchPagePath"
              @click="close"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {{ t('colivingNaturalSearch.advancedSearch') }}
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </router-link>

            <span class="text-gray-200 select-none">·</span>

            <button
              @click="handlePartnerServicesClick"
              class="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-500 hover:text-indigo-700 transition-colors"
            >
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {{ t('navigation.partnerServices') }}
            </button>
          </div>

          <!-- Pills de filtros eliminados por diseño -->

          <!-- Estado de carga -->
          <div v-if="isSearching" class="mt-4 flex flex-col items-center py-8">
            <svg class="h-7 w-7 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <p class="text-sm text-gray-400 mt-3">{{ t('colivingNaturalSearch.searching') }}</p>
          </div>

          <!-- ── Resultados (Top 3) ── -->
          <div v-if="hasResults && !isSearching" class="mt-4">
            <div class="flex items-center justify-between mb-3">
              <p class="text-xs font-medium text-gray-400 uppercase tracking-wider">
                {{ t('colivingNaturalSearch.topResults', { count: totalResults }) }}
              </p>
            </div>

            <div class="space-y-2">
              <div
                v-for="result in topResults"
                :key="result.id"
                @click="handleResultClick(result)"
                class="flex gap-3 p-2.5 rounded-xl border border-gray-200 
                       hover:border-gray-300 hover:shadow-sm transition-all duration-200 
                       cursor-pointer group bg-white"
              >
                <!-- Imagen miniatura -->
                <div 
                  class="w-[72px] h-[72px] rounded-lg bg-gray-100 bg-cover bg-center flex-shrink-0 overflow-hidden"
                  :style="{ backgroundImage: getResultImage(result) ? `url('${getResultImage(result)}')` : 'none' }"
                >
                  <div v-if="!getResultImage(result)" class="w-full h-full flex items-center justify-center">
                    <svg class="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5Z" />
                    </svg>
                  </div>
                </div>

                <!-- Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <h4 class="text-sm font-semibold text-gray-900 truncate group-hover:text-gray-700 transition-colors">
                        {{ result.title || t('colivingNaturalSearch.untitledColiving') }}
                      </h4>
                      <!-- Badge DESTACADO para agentes PRO -->
                      <div v-if="result.isProAgent" class="mt-0.5">
                        <span class="inline-flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-green-600 px-1.5 py-0.5 rounded text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span class="text-[10px] font-bold">DESTACADO</span>
                        </span>
                      </div>
                    </div>
                    <!-- Badge externo -->
                    <span v-if="result.isExternal" class="flex-shrink-0 text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {{ getProviderLabel(result) }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 mt-0.5 truncate">
                    {{ result.propertyAddress || result.propertyLocation || '' }}
                  </p>
                  <div class="flex items-center gap-2 mt-1.5">
                    <span class="text-sm font-bold text-gray-900">
                      {{ getResultPrice(result) }}
                    </span>
                    <span v-if="result.availableRooms" class="text-[11px] text-gray-400 font-medium">
                      {{ result.availableRooms }} {{ t('colivingNaturalSearch.roomsAvailable') }}
                    </span>
                  </div>
                  <!-- Match badge -->
                  <div v-if="result.matchData && result.matchData.score >= 50" class="mt-1">
                    <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      :class="result.matchData.score >= 80 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'bg-amber-50 text-amber-700'"
                    >
                      Match {{ result.matchData.score }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Divider -->
            <div class="h-px bg-gray-200 my-3"></div>

            <!-- Ver más resultados (estilo CTA de HeroLanding) -->
            <button
              @click="viewAllResults"
              class="w-full py-2.5 text-sm font-semibold text-gray-900 hover:text-indigo-600 
                     transition-colors flex items-center justify-center gap-2 group/cta"
            >
              {{ t('colivingNaturalSearch.viewMore', { count: totalResults }) }}
              <svg class="w-4 h-4 transition-transform group-hover/cta:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>

          <!-- Sin resultados -->
          <div v-if="searchPerformed && !hasResults && !isSearching" class="mt-4 text-center py-8">
            <div class="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p class="text-sm font-medium text-gray-900">{{ t('colivingNaturalSearch.noResults') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ t('colivingNaturalSearch.noResultsHint') }}</p>
          </div>
        </div>
      </div>

      <!-- ── Footer: CTA de login (solo si no autenticado) ── -->
      <div v-if="!isAuthenticated" class="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-2.5">
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs font-bold text-gray-900 tracking-tight">{{ t('colivingNaturalSearch.loginCta.title') }}</p>
          <router-link 
            to="/signup"
            class="text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 
                   px-3.5 py-1.5 rounded-full transition-colors flex-shrink-0"
            @click="close"
          >
            {{ t('colivingNaturalSearch.loginCta.signup') }}
          </router-link>
        </div>
      </div>
      </template>
    </div>
  </Transition>

  <!-- Modal de lead / partner (solo cuando el padre no delega) -->
  <PartnerContactModal
    v-if="!delegateModalsToParent"
    :isVisible="isPartnerModalOpen"
    @close="isPartnerModalOpen = false"
  />

  <ScrapedColivingLeadModal
    v-if="!delegateModalsToParent && isLeadModalOpen"
    :show="isLeadModalOpen"
    mode="listing"
    :coliving="leadModalColiving"
    :city="leadModalColiving?.propertyLocation || ''"
    @close="isLeadModalOpen = false; leadModalColiving = null"
    @submitted="isLeadModalOpen = false; leadModalColiving = null"
  />

  <HiveLeadModal
    v-if="!delegateModalsToParent"
    :show="isHiveLeadModalOpen"
    :coliving="hiveLeadColiving"
    @close="isHiveLeadModalOpen = false; hiveLeadColiving = null"
    @submitted="isHiveLeadModalOpen = false; hiveLeadColiving = null"
  />
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import axios from 'axios';
import AssistantWidgetPanel from './AssistantWidgetPanel.vue';
import {
  parseColivingNaturalLanguage,
  filtersToSearchParams,
  filtersToRouteQuery,
} from '@/utils/colivingNaturalLanguageParser';
import PartnerContactModal from './PartnerContactModal.vue';
import ScrapedColivingLeadModal from './ScrapedColivingLeadModal.vue';
import HiveLeadModal from './HiveLeadModal.vue';
import { trackInteraction } from '@/utils/trackInteraction';
import { notifyPartnerServicesClick } from '@/utils/notifyPartnerServicesClick';
import { useRegion } from '@/composables/useRegion';
import { isAffiliatePartnerListing, openAffiliatePartnerListing } from '@/utils/externalRedirect';

const props = defineProps({
  delegateModalsToParent: { type: Boolean, default: false },
});

const SEARCH_LAYOUT_DESKTOP_MQ = '(min-width: 1024px)';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { regionId, formatPrice, regionMainCity } = useRegion();

// Ruta del buscador según la región activa
const searchPagePath = computed(() =>
  regionId.value === 'us' || regionId.value === 'uk'
    ? '/findyourcoliving'
    : '/encuentratucoliving'
);

// Placeholder dinámico según la región
const searchPlaceholder = computed(() => {
  if (regionId.value === 'us') return t('colivingNaturalSearch.placeholderUS');
  if (regionId.value === 'uk') return t('colivingNaturalSearch.placeholderUK');
  return t('colivingNaturalSearch.placeholder');
});

// ── Estado del widget ──
// Cerrado por defecto; solo auto-abrir en desktop (matchMedia en onMounted, no innerWidth en setup)
const isOpen = ref(false);
const hasBeenClosed = ref(false);

/** Layout compacto del buscador (&lt; lg). En móvil: panel cerrado + botón flotante. */
const isCompactSearchViewport = ref(
  typeof window !== 'undefined' ? !window.matchMedia(SEARCH_LAYOUT_DESKTOP_MQ).matches : true,
);

const showCollapsedTrigger = computed(() => {
  if (isOpen.value) return false;
  if (isCompactSearchViewport.value) return true;
  return hasBeenClosed.value;
});

let searchLayoutMq = null;
let onSearchLayoutChange = null;
const searchText = ref('');
const detectedFilters = ref(null);
const isSearching = ref(false);
const searchPerformed = ref(false);
const topResults = ref([]);
const totalResults = ref(0);

// ── Auth y preferencias ──
const isAuthenticated = ref(false);
const currentUser = ref(null);
const userPreferences = ref(null);

// Estado del modal externo eliminado (ya no se usa - click directo)

const searchInputRef = ref(null);
const contentRef = ref(null);
let debounceTimer = null;
let authUnsubscribe = null;

// ── Detección de modo: asistente vs búsqueda ──
const userType = ref('');
const urgentCount = ref(0);
// null = aún cargando, true/false = valor real de Firestore
const userEmailVerified = ref(null);

// Muestra el aviso de verificación de email para applicants sin verificar
const showEmailVerificationPrompt = computed(() => {
  return isAuthenticated.value &&
    userType.value === 'applicant' &&
    userEmailVerified.value === false;
});

const isAssistantMode = computed(() => {
  if (!isAuthenticated.value || userType.value !== 'applicant') return false;
  // No mostrar asistente hasta saber si el email está verificado
  if (userEmailVerified.value !== true) return false;
  const path = route.path;
  return path === '/contracts' || path.startsWith('/contracts/');
});

function handleUrgentCount(count) {
  urgentCount.value = count;
}

// ── Computados ──
const hasResults = computed(() => topResults.value.length > 0);
const hasPreferences = computed(() => {
  if (!userPreferences.value) return false;
  return userPreferences.value.preferredCity || userPreferences.value.monthlyIncome || userPreferences.value.isStudent;
});

// ── Mapa de proveedores para labels y slugs ──
const providerDisplayNames = {
  urbancampus: 'Urban Campus', habyt: 'Habyt', muppy: 'Muppy', kanso: 'Kanso',
  enso: 'Enso', homiii: 'Homiii', spacest: 'Spacest', aticco: 'Aticco',
  haaus: 'Haaus', socien: 'Socien', suiters: 'Suiters', houxury: 'Houxury',
  colivinn: 'ColivINN', flipco: 'Flipco', liveit: 'Live it', queens: 'Queens',
  socialhub: 'The Social Hub', outsite: 'Outsite', resa: 'RESA', yugo: 'Yugo',
  livensa: 'Livensa', collegiate: 'Collegiate', flaternity: 'Flaternity',
  hive: 'Grupo Hive', nomad: 'Nomad', repeople: 'Repeople', maraya: 'Maraya',
  farhome: 'Far Home', node: 'Node Living', trees: 'Trees', gaiastays: 'GaiaStays',
  mycoliving: 'MyCO Living', trc: 'TRC', becasa: 'Be Casa', oceanika: 'Oceanika',
  nova: 'Nova Málaga', alegoria: 'Alegoría', olivar: 'Olivar Plaza', thestart: 'The Start',
};


// ══════════════════════════════════════════════════
// CICLO DE VIDA
// ══════════════════════════════════════════════════

onMounted(() => {
  searchLayoutMq = window.matchMedia(SEARCH_LAYOUT_DESKTOP_MQ);
  onSearchLayoutChange = () => {
    const enteringCompact = !searchLayoutMq.matches;
    isCompactSearchViewport.value = enteringCompact;
    if (enteringCompact) {
      isOpen.value = false;
    } else if (!hasBeenClosed.value) {
      isOpen.value = true;
    }
  };
  isCompactSearchViewport.value = !searchLayoutMq.matches;
  if (!isCompactSearchViewport.value && !hasBeenClosed.value) {
    isOpen.value = true;
  }
  searchLayoutMq.addEventListener('change', onSearchLayoutChange);

  const auth = getAuth();
  authUnsubscribe = onAuthStateChanged(auth, async (user) => {
    isAuthenticated.value = !!user;
    currentUser.value = user;
    if (user) {
      // Cargar preferencias y tipo de usuario en paralelo
      const userDocPromise = getDoc(doc(db, 'users', user.uid));
      await loadUserPreferences(user.uid);
      try {
        const userDoc = await userDocPromise;
        if (userDoc.exists()) {
          const data = userDoc.data();
          userType.value = data.userType || '';
          // Leer emailVerified del documento de Firestore (sistema de verificación custom)
          userEmailVerified.value = data.emailVerified === true ? true : false;
        }
      } catch (e) {
        console.warn('Error loading userType:', e);
        userEmailVerified.value = null;
      }
    } else {
      userPreferences.value = null;
      userType.value = '';
      userEmailVerified.value = null;
    }
  });
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  if (searchLayoutMq && onSearchLayoutChange) {
    searchLayoutMq.removeEventListener('change', onSearchLayoutChange);
  }
  if (authUnsubscribe) authUnsubscribe();
  if (debounceTimer) clearTimeout(debounceTimer);
  document.removeEventListener('keydown', handleKeydown);
});

// ── Cargar preferencias del usuario ──
async function loadUserPreferences(uid) {
  try {
    const cached = localStorage.getItem('userSearchPreferences');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.preferences && Date.now() - parsed.timestamp < 30 * 60 * 1000) {
        userPreferences.value = parsed.preferences;
        return;
      }
    }
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      userPreferences.value = {
        preferredCity: data.preferredCity || null,
        isStudent: data.isStudent || false,
        monthlyIncome: data.monthlyIncome || null,
      };
      localStorage.setItem('userSearchPreferences', JSON.stringify({
        preferences: userPreferences.value,
        timestamp: Date.now(),
      }));
    }
  } catch (error) {
    console.error('Error loading user preferences:', error);
  }
}

// ══════════════════════════════════════════════════
// ABRIR / CERRAR
// ══════════════════════════════════════════════════

function open() {
  isOpen.value = true;
  nextTick(() => {
    searchInputRef.value?.focus();
  });
}

function close() {
  isOpen.value = false;
  hasBeenClosed.value = true;
}

function handleKeydown(e) {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
}

// ══════════════════════════════════════════════════
// INPUT Y PARSING
// ══════════════════════════════════════════════════

function handleInput() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    parseInput();
  }, 400);
}

function handleEnter() {
  if (searchText.value.trim()) {
    executeSearch();
  }
}

function parseInput() {
  if (!searchText.value.trim()) {
    detectedFilters.value = null;
    return;
  }
  detectedFilters.value = parseColivingNaturalLanguage(
    searchText.value,
    isAuthenticated.value ? userPreferences.value : null
  );
}

// ══════════════════════════════════════════════════
// BÚSQUEDA
// ══════════════════════════════════════════════════

async function executeSearch() {
  if (!searchText.value.trim()) return;
  parseInput();

  if (!detectedFilters.value || Object.keys(detectedFilters.value).length === 0) {
    detectedFilters.value = {};
  }

  isSearching.value = true;
  searchPerformed.value = true;
  topResults.value = [];
  totalResults.value = 0;

  try {
    const params = filtersToSearchParams(detectedFilters.value);
    if (currentUser.value) {
      params.userId = currentUser.value.uid;
    }
    // Pasar región activa para que la API filtre colivings correctamente
    params.region = regionId.value;

    const apiUrl = process.env.VUE_APP_SEARCH_API_URL;
    if (!apiUrl) {
      console.error('VUE_APP_SEARCH_API_URL not defined');
      return;
    }

    const response = await axios.get(apiUrl, { params });

    if (response.data && Array.isArray(response.data.results)) {
      const results = response.data.results.filter(c => !c.assignedApplicant);
      topResults.value = results.slice(0, 3);
      totalResults.value = response.data.totalResults || results.length;

      trackInteraction('nl_search', { query: searchText.value.slice(0, 200), resultsCount: totalResults.value })
      if (totalResults.value === 0) {
        trackInteraction('nl_search_zero', { query: searchText.value.slice(0, 200) })
      }
    }
  } catch (error) {
    console.error('Search error:', error);
  } finally {
    isSearching.value = false;
    nextTick(() => {
      if (contentRef.value) {
        contentRef.value.scrollTop = contentRef.value.scrollHeight;
      }
    });
  }
}

// ══════════════════════════════════════════════════
// CLICK EN RESULTADO: directo (igual que ContractCard.vue)
// ══════════════════════════════════════════════════

function handleResultClick(result) {
  // 1. Track para estadísticas generales de widget (interactions)
  trackInteraction('nl_search_click', {
    service: result.id,
    source: result.isExternal ? 'external' : 'native',
  })
  
  // 2. Track para estadísticas de clicks (searchClicks) - CRÍTICO para emails y AdminAnalytics
  trackSearchClick(result)
  
  // 3. Redirigir según tipo
  if (result.isHivePartner) {
    if (props.delegateModalsToParent) {
      emit('request-scraped-lead', result);
    } else {
      hiveLeadColiving.value = result;
      isHiveLeadModalOpen.value = true;
    }
    return;
  }

  if (result.isExternal) {
    if (isAffiliatePartnerListing(result)) {
      openAffiliatePartnerListing(result, { source: 'widget' });
      close();
      return;
    }
    if (props.delegateModalsToParent) {
      emit('request-scraped-lead', result);
    } else {
      leadModalColiving.value = result;
      isLeadModalOpen.value = true;
    }
  } else {
    close();
    router.push(`/colivings/${result.id}`);
  }
}

// Helper: registrar click en analytics/searchClicks (igual que ContractCard.vue)
function trackSearchClick(result) {
  try {
    const payload = {
      colivingId: result.id,
      colivingTitle: result.title || result.propertyTitle || '',
      isExternal: !!result.isExternal,
      position: undefined, // Widget no tiene posición en grid
    }
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/search-click', blob)
    } else {
      fetch('/api/analytics/search-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* non-blocking */ })
    }
  } catch { /* non-blocking */ }
}

// ── Ver todos los resultados ──
function viewAllResults() {
  if (!detectedFilters.value) return;
  const query = filtersToRouteQuery(detectedFilters.value);
  close();

  if (router.currentRoute.value.path.startsWith('/encuentratucoliving') ||
      router.currentRoute.value.path.startsWith('/findyourcoliving')) {
    emit('apply-filters', detectedFilters.value);
  } else {
    router.push({ path: searchPagePath.value, query });
  }
}

// ══════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════

function getResultImage(result) {
  if (result.images && result.images.length > 0) {
    const img = result.images[0];
    return typeof img === 'string' ? img : img.url || img.src || null;
  }
  return null;
}

function getResultPrice(result) {
  const price = result.priceFrom || result.propertyPrice;
  if (!price) return t('colivingNaturalSearch.priceOnRequest');
  return `${formatPrice(price)}${t('contractCard.perMonth')}`;
}

function getProviderLabel(result) {
  const source = result.externalSource || '';
  return providerDisplayNames[source.toLowerCase()] || source || 'Externo';
}

// ── Lead Modal (colivings scrapeados / Hive, solo si no delega al padre) ──
const isLeadModalOpen = ref(false);
const leadModalColiving = ref(null);
const isHiveLeadModalOpen = ref(false);
const hiveLeadColiving = ref(null);

// ── Partner Services ──
const isPartnerModalOpen = ref(false);

function handlePartnerServicesClick() {
  notifyPartnerServicesClick('widget', currentUser.value);
  trackInteraction('cta_click', { ctaName: 'partner_services_widget' });
  if (props.delegateModalsToParent) {
    emit('open-partner-services');
  } else {
    isPartnerModalOpen.value = true;
  }
}

// ── Emits ──
const emit = defineEmits(['apply-filters', 'request-scraped-lead', 'request-hive-lead', 'open-partner-services']);
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
